import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import { Box, Button, IconButton, Typography } from '@mui/material'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import AddIcon from '@mui/icons-material/Add'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'

// hooks
import useGroup from '../../hooks/useGroup'
import useUsers from '../../hooks/useUsers'
import useDialogVisibility from '../../hooks/useDialogVisibility'

// components
import SearchBarComponent from '../../components/searchBarComponent'
import GroupMembers from './components/groupMembers'
import SubHeaderActions from './components/subHeaderActions'
import AddMembersModal from './components/addMembersModal'
import ButtonWithLoader from '../../components/buttonWithLoader'
import ConfirmationDialog from '../../components/confirmationDialog'
import { useIsMobileView } from '../../utils/isMobileView'
import Breadcrumbs from '../../components/Breadcrumbs'

import successIcon from '../../assets/Checkmark-filled.svg'
import errorIcon from '../../assets/error-icon.svg'

// helpers
import { makeEditGroupDTO } from './components/subHeaderActions/subHeaderActions.helpers'

// services
import { updateGroup } from '../../services/groups'

const EditMembers = () => {
  const { t } = useTranslation()
  const isMobileView = useIsMobileView()
  const { groupId } = useParams()
  const { group, isGroupLoading, setGroup, getGroup } = useGroup(groupId)
  const { users: allUsers } = useUsers()
  const [searchText, setSearchText] = useState('')
  const [selectedMembers, setSelectedMembers] = useState([])
  const [removedMembers, setRemovedMembers] = useState([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [hasError, setHasError] = useState(false)
  const [isRemoveMembersClicked, setIsRemoveMembersClicked] = useState(false)
  const [showAddMembersModal, setShowAddMembersModal] = useState(false)
  const [
    isRemoveConfirmationDialogOpen,
    openRemoveConfirmationDialog,
    closeRemoveConfirmationDialog,
  ] = useDialogVisibility(false)
  const [
    isCancelConfirmationDialogOpen,
    openCancelConfirmationDialog,
    closeCancelConfirmationDialog,
  ] = useDialogVisibility(false)

  const handleAddMembersClick = () => {
    setShowAddMembersModal(true)
  }

  const handleUpdateGroupFailure = (error) => {
    toast.error(error?.message)
    setHasError(true)
  }

  const handleCancel = () => {
    if (selectedMembers?.length > 0) {
      openCancelConfirmationDialog()
    } else {
      setIsRemoveMembersClicked(false)
      setSelectedMembers([])
    }
  }

  const handleRemoveConfirmation = async () => {
    setIsSubmitting(true)

    const removed =
      group?.members?.filter((member) =>
        selectedMembers.includes(member.value)
      ) || []

    const remainingMembers = group?.members?.filter(
      (member) => !selectedMembers.includes(member.value)) || []
    const removedNames = removed.map((member) => member.display)
    const groupDTO = makeEditGroupDTO(selectedMembers)

    try {
      await updateGroup(groupDTO, groupId)
      setGroup((previousGroup) => ({
        ...previousGroup,
        members: remainingMembers,
        stats: {
          ...previousGroup.stats,
          totalMemberCount: remainingMembers.length
        },
      }))

      setSelectedMembers([])
      setIsRemoveMembersClicked(false)
      setRemovedMembers(removedNames)
    } catch (error) {
      handleUpdateGroupFailure(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleRemove = () => {
    openRemoveConfirmationDialog()
  }

  const handleSubmitRemoveConfirmationDialog = () => {
    closeRemoveConfirmationDialog()
    handleRemoveConfirmation()
  }

  const handleSubmitCancelConfirmationDialog = () => {
    setIsRemoveMembersClicked(false)
    setSelectedMembers([])
    closeCancelConfirmationDialog()
  }

  const handleCloseMessage = (isSuccess) => {
    if (isSuccess === true) {
      setRemovedMembers([])
    } else {
      setHasError(false)
    }
  }

  const { stats } = group
  const totalMembersCount = stats?.totalMemberCount

  const breadcrumbPath = [
    { label: 'groups-edit-tab', path: '/search-group' },
    { label: `${group?.displayName}`, path: `/view-group/${groupId}` },
    { label: 'edit-members-sub-header-title' }
  ]

  return (
    <>
      {(!isMobileView && !isGroupLoading) && (
      <div
        style={{ backgroundColor: '#F5F5F5', width: '100vw', padding: '20px' }}
      >

        <Breadcrumbs crumbs={breadcrumbPath} />
      </div>
      )}
      <Box
        sx={{
          marginLeft: isMobileView ? '51px' : '101px',
          marginBottom: '296px',
        }}
      >
        <SubHeaderActions groupName={group?.displayName} />
        {removedMembers.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '10px',
              backgroundColor: '#53CE7633',
              padding: '4px',
              marginTop: '12px',
              maxWidth: '867px',
              marginBottom: '23px',
              fontWeight: 'bold',
            }}
          >
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px'
            }}
            >
              <img
                src={successIcon}
                alt=""
                style={{
                  mt: -2.5,
                  height: '25px',
                  width: '25px',
                  padding: '3px',
                }}
              />
              <Typography style={{ fontSize: '14px' }}>
                {`${t(
                  'edit-members-success-remove-message'
                )} ${removedMembers.join(', ')}`}
              </Typography>
            </Box>{' '}
            <IconButton
              aria-label="close"
              onClick={() => handleCloseMessage(true)}
            >
              <CloseRoundedIcon />
            </IconButton>
          </Box>
        )}
        {hasError && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '10px',
              backgroundColor: '#FF601738',
              padding: '4px',
              marginTop: '12px',
              maxWidth: '867px',
              marginBottom: '23px',
              fontWeight: 'bold',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
              }}
            >
              <img
                src={errorIcon}
                alt=""
                style={{
                  mt: -2.5,
                  height: '25px',
                  width: '25px',
                  padding: '3px',
                }}
              />
              <Typography style={{ fontSize: '14px' }}>
                {t('edit-members-error-remove-message')}
              </Typography>
            </Box>
            <IconButton
              aria-label="close"
              onClick={() => handleCloseMessage(false)}
            >
              <CloseRoundedIcon />
            </IconButton>
          </Box>
        )}
        <Box
          sx={{
            display: 'flex',
            gap: isMobileView ? '30px' : '115px',
            alignItems: isMobileView ? 'flex-start' : 'center',
            flexDirection: isMobileView ? 'column' : 'row',
            backgroundColor: '#F9F8F7',
            padding: '26px',
            maxWidth: '867px',
            marginRight: isMobileView ? '51px' : '0',
            marginBottom: '23px',
          }}
        >
          {stats && totalMembersCount <= 1000 && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontFamily: 'Johnson Text',
                  fontSize: '16px',
                  fontWeight: 500,
                }}
              >
                {t('edit-members-find-members')}
              </Typography>
              <SearchBarComponent
                searchText={searchText}
                setSearchText={setSearchText}
                data-testid="searchBarComponent"
                isModal={false}
                placeholderText={
                  isMobileView
                    ? 'edit-members-search-placeholder-mobile'
                    : 'edit-members-search-placeholder'
                }
              />
            </Box>
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddMembersClick}
            sx={{
              backgroundColor: '#000000',
              maxWidth: '223px',
              height: '40px',
              borderRadius: '333px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              padding: '0 10px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              marginTop: '30px',
              '&:hover': {
                backgroundColor: '#000000',
              },
            }}
          >
            {t('edit-members-add-member-button')}
          </Button>
        </Box>

        <GroupMembers
          group={group}
          searchText={searchText}
          allUsers={allUsers}
          isLoading={isGroupLoading}
          isRemoveMembersClicked={isRemoveMembersClicked}
          selectedMembers={selectedMembers}
          setSelectedMembers={setSelectedMembers}
        />

        <Box
          sx={{
            display: 'flex',
            marginTop: '40px',
            marginBottom: '20px',
            gap: '24px',
          }}
        >
          <ButtonWithLoader
            onClick={handleRemove}
            loading={isSubmitting}
            disabled={selectedMembers.length === 0}
            label={isSubmitting ? '' : t('edit-members-remove-button')}
            sx={{
              minWidth: '156px',
              height: '48px',
              borderRadius: '333px',
              backgroundColor: '#EB1700',
              padding: '0 16px',
              fontSize: '1rem',
              '&:hover': {
                backgroundColor: '#EB1700',
              },
            }}
          />
          <Button
            onClick={handleCancel}
            type="submit"
            variant="contained"
            disableElevation
            sx={{
              minWidth: '102px',
              fontSize: '1rem',
              color: '#000000',
              height: '48px',
              borderRadius: '333px',
              backgroundColor: '#6251371A',
              '&:hover': {
                backgroundColor: '#6251371A',
              },
            }}
          >
            {t('cancel')}
          </Button>
          <ConfirmationDialog
            showDialog={isRemoveConfirmationDialogOpen}
            onSubmit={handleSubmitRemoveConfirmationDialog}
            onCancel={closeRemoveConfirmationDialog}
            primaryBtnText={t('edit-members-confirmation-modal-primary-button')}
            secondaryBtnText={t('cancel')}
            title={t('edit-members-confirmation-modal-title')}
            content={t('edit-members-confirmation-modal-description')}
            hasNewDesign
          />
          <ConfirmationDialog
            showDialog={isCancelConfirmationDialogOpen}
            onSubmit={handleSubmitCancelConfirmationDialog}
            onCancel={closeCancelConfirmationDialog}
          />
        </Box>

        <AddMembersModal
          showModal={showAddMembersModal}
          setShowModal={setShowAddMembersModal}
          group={group}
          setGroup={setGroup}
          getGroup={getGroup}
        />
      </Box>
    </>
  )
}

export default EditMembers
